import React, { useState, useEffect } from 'react';
import { DataCategory, ValidationField } from '../../types';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Loader2, FileText, Pencil, Check, X } from 'lucide-react';
import { Button } from '../ui/Button';

interface FieldInstructionsProps {
  category: DataCategory;
  subcategory?: string;
  refreshTrigger?: number;
}

const EDITABLE_COLUMN_KEYS = new Set(['remarks', 'Remarks']);

const FieldInstructions: React.FC<FieldInstructionsProps> = ({ category, subcategory, refreshTrigger }) => {
  const { hasPermission } = useAuth();
  const canEditInstructions = hasPermission('Field Instructions', 'can_update');
  const [fields, setFields] = useState<ValidationField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{ rowKey: string; key: string } | null>(null);
  const [cellDraft, setCellDraft] = useState<string>('');
  const [cellError, setCellError] = useState<string | null>(null);
  const [viewOverrides, setViewOverrides] = useState<Record<string, string>>({});

  const VIEW_STORAGE_PREFIX = 'fieldInstruction_view_';
  const VIEW_SEP = '___';

  const cellId = (rowKey: string, colKey: string) => `${rowKey}__${colKey}`;

  const getViewStorageKey = (rowKey: string, columnKey: string) =>
    `${VIEW_STORAGE_PREFIX}${subcategory || category}_${rowKey}${VIEW_SEP}${columnKey}`;

  const loadViewOverrides = () => {
    if (!subcategory && !category) return;
    const prefix = `${VIEW_STORAGE_PREFIX}${subcategory || category}_`;
    const out: Record<string, string> = {};
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) {
          const suffix = k.slice(prefix.length);
          const parts = suffix.split(VIEW_SEP);
          if (parts.length >= 2) {
            const rowKey = parts.slice(0, -1).join(VIEW_SEP);
            const columnKey = parts[parts.length - 1];
            const id = cellId(rowKey, columnKey);
            const val = localStorage.getItem(k);
            if (val != null) out[id] = val;
          }
        }
      }
    } catch (_) {}
    setViewOverrides(out);
  };

  useEffect(() => {
    loadViewOverrides();
  }, [subcategory, category]);

  const [visibilityByKey, setVisibilityByKey] = useState<Record<string, boolean>>({});
  const [visibilityLoading, setVisibilityLoading] = useState(false);

  useEffect(() => {
    const fetchInstructionsAndSubcategory = async () => {
      try {
        setLoading(true);
        setError(null);

        if (subcategory) {
          const response = await api.getInstructionsBySubcategory(subcategory);
          setFields(response.data || response.instructions || response.fields || []);
        } else {
          const response = await api.getInstructions(category);
          setFields(response.data || response.fields || []);
        }
      } catch (err) {
        setError('Failed to load field instructions');
        console.error('Error fetching instructions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInstructionsAndSubcategory();
  }, [category, subcategory, refreshTrigger]);

  useEffect(() => {
    const fetchVisibility = async () => {
      if (!subcategory || fields.length === 0) return;
      try {
        const res: any = await api.getFieldVisibility(subcategory);
        // Backend returns { data: [{field_name, is_visible}, ...] } or { visibility: {...} }
        let vis: Record<string, boolean> = {};
        if (Array.isArray(res.data)) {
          for (const row of res.data) {
            const fn = String(row.field_name || '');
            if (fn) vis[fn] = row.is_visible !== false;
          }
        } else {
          vis = res.visibility || res.data || {};
        }
        const next: Record<string, boolean> = {};
        fields.forEach(f => {
          const key = getRowKey(f);
          if (key) next[key] = vis[key] !== false;
        });
        setVisibilityByKey(next);
      } catch (_) {
        const next: Record<string, boolean> = {};
        fields.forEach(f => {
          const key = getRowKey(f);
          if (key) next[key] = true;
        });
        setVisibilityByKey(next);
      }
    };
    fetchVisibility();
  }, [subcategory, fields]);


  // Get all available field keys from the first field to create dynamic columns
  const getFieldKeys = () => {
    if (fields.length === 0) return [];
    return Object.keys(fields[0]);
  };

  // Get display name for field key
  const getFieldDisplayName = (key: string) => {
    const displayNames: { [key: string]: string } = {
      sap_field_name: 'SAP Field Name',
      db_field_name: 'Database Field Name',
      description: 'Description',
      data_type: 'Data Type',
      field_length: 'Field Length',
      is_mandatory: 'Mandatory',
      valid_values: 'Valid Values',
      related_table: 'Related Table',
      remarks: 'Remarks',
      instruction_image_path: 'Instruction Image Path',
      table_name: 'Table Name'
    };
    return displayNames[key] || key;
  };

  const getRowKey = (field: ValidationField): string => {
    return (field.sap_field_name ?? (field as any).SAPFields ?? '') as string;
  };

  const getRawValue = (field: ValidationField, key: string): string | number | boolean | string[] => {
    const v = field[key];
    if (v === null || v === undefined) return '';
    if (key === 'is_mandatory' || key === 'Mandatory') return Boolean(v);
    if (key === 'valid_values' || key === 'ValidValues') return Array.isArray(v) ? v : String(v);
    return typeof v === 'object' ? String(v) : v;
  };

  const isEditableColumn = (key: string) => EDITABLE_COLUMN_KEYS.has(key) && canEditInstructions;

  const handleStartEditCell = (field: ValidationField, key: string) => {
    const rowKey = getRowKey(field);
    setEditingCell({ rowKey, key });
    const raw = getRawValue(field, key);
    if (Array.isArray(raw)) setCellDraft(raw.join(', '));
    else if (typeof raw === 'boolean') setCellDraft(raw ? 'Yes' : 'No');
    else setCellDraft(String(raw ?? ''));
    setCellError(null);
  };

  const handleCancelEditCell = () => {
    setEditingCell(null);
    setCellDraft('');
    setCellError(null);
  };

  const handleSaveCell = async () => {
    if (!editingCell) return;
    const { rowKey, key } = editingCell;
    const id = cellId(rowKey, key);
    const trimmed = cellDraft.trim();
    const storageKey = getViewStorageKey(rowKey, key);
    const isRemarks = key === 'remarks' || key === 'Remarks';

    try {
      if (trimmed) {
        localStorage.setItem(storageKey, trimmed);
        setViewOverrides(prev => ({ ...prev, [id]: trimmed }));
      } else {
        localStorage.removeItem(storageKey);
        setViewOverrides(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }

      if (isRemarks && subcategory && rowKey) {
        setCellError(null);
        await api.updateFieldInstructionRemark(subcategory, rowKey, trimmed);
        setViewOverrides(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        localStorage.removeItem(storageKey);
        const response = await api.getInstructionsBySubcategory(subcategory);
        setFields(response.data || response.instructions || response.fields || []);
      }
    } catch (err) {
      if (isRemarks && subcategory && rowKey) {
        setCellError(err instanceof Error ? err.message : 'Failed to save to server');
        return;
      }
    }
    setEditingCell(null);
    setCellDraft('');
    setCellError(null);
  };

  const getDisplayValue = (field: ValidationField, key: string): string => {
    const rowKey = getRowKey(field);
    const id = cellId(rowKey, key);
    const override = viewOverrides[id];
    if (override !== undefined && override !== '') return override;
    const raw = getRawValue(field, key);
    if (Array.isArray(raw)) return raw.join(', ');
    if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
    return String(raw ?? '');
  };

  const [visibilityError, setVisibilityError] = useState<string | null>(null);

  const handleVisibilityChange = async (fieldKey: string, showInValidation: boolean) => {
    if (!subcategory || !fieldKey) return;
    setVisibilityError(null);
    const previous = visibilityByKey[fieldKey];
    setVisibilityByKey(prev => ({ ...prev, [fieldKey]: showInValidation }));
    try {
      await api.updateFieldVisibility(subcategory, { fieldKey, showInValidation });
    } catch (err) {
      console.error('Failed to update visibility', err);
      setVisibilityByKey(prev => ({ ...prev, [fieldKey]: previous === undefined ? true : previous }));
      setVisibilityError(err instanceof Error ? err.message : 'Failed to update. Check that field_visibility table exists.');
    }
  };

  const handleSelectAllVisibility = async (checked: boolean) => {
    if (!subcategory || fields.length === 0) return;
    setVisibilityError(null);
    const previous = { ...visibilityByKey };
    const updates = fields.map(f => ({ fieldKey: getRowKey(f), showInValidation: checked })).filter(u => u.fieldKey);
    const next: Record<string, boolean> = {};
    updates.forEach(u => { next[u.fieldKey] = u.showInValidation; });
    setVisibilityByKey(next);
    setVisibilityLoading(true);
    try {
      await api.updateFieldVisibility(subcategory, { updates });
    } catch (err) {
      console.error('Failed to update visibility', err);
      setVisibilityByKey(previous);
      setVisibilityError(err instanceof Error ? err.message : 'Failed to update. Check that field_visibility table exists.');
    } finally {
      setVisibilityLoading(false);
    }
  };

  // Render field value: Remarks save to DB; other editable columns use view override (localStorage)
  const renderFieldValue = (field: ValidationField, key: string, rowIndex: number) => {
    const rowKey = getRowKey(field);
    const id = cellId(rowKey, key);
    const isEditing = editingCell?.rowKey === rowKey && editingCell?.key === key;
    const isEditable = isEditableColumn(key);

    if (isEditable && isEditing) {
      const isLong = key === 'remarks' || key === 'Remarks';
      const isMandatory = key === 'is_mandatory' || key === 'Mandatory';
      return (
        <div className="flex flex-col gap-2 min-w-[140px]">
          {isLong ? (
            <textarea
              value={cellDraft}
              onChange={e => setCellDraft(e.target.value)}
              className="w-full min-h-[80px] px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="..."
              autoFocus
            />
          ) : isMandatory ? (
            <select
              value={cellDraft}
              onChange={e => setCellDraft(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
              autoFocus
            >
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          ) : (
            <input
              type="text"
              value={cellDraft}
              onChange={e => setCellDraft(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="-"
              autoFocus
            />
          )}
          {cellError && <p className="text-xs text-red-600">{cellError}</p>}
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSaveCell} className="flex items-center gap-1">
              <Check className="w-3 h-3" />
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancelEditCell} className="flex items-center gap-1">
              <X className="w-3 h-3" />
              Cancel
            </Button>
          </div>
        </div>
      );
    }

    const displayVal = isEditable ? getDisplayValue(field, key) : (field[key] != null ? String(field[key]) : '');
    const isEmpty = displayVal === '';

    if (key === 'is_mandatory' || key === 'Mandatory') {
      const yesNo = displayVal.toLowerCase() === 'yes' || displayVal === 'true';
      return (
        <div className="flex items-center gap-2 group">
          <Badge variant={yesNo ? 'success' : 'secondary'}>{yesNo ? 'Yes' : 'No'}</Badge>
          {isEditable && (
            <button
              type="button"
              onClick={() => handleStartEditCell(field, key)}
              className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Edit (view only, saved in browser)"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      );
    }

    if ((key === 'valid_values' || key === 'ValidValues') && displayVal && displayVal.includes(',')) {
      const items = displayVal.split(',').map(s => s.trim()).filter(Boolean);
      return (
        <div className="flex items-center gap-2 group flex-wrap">
          <div className="flex flex-wrap gap-1 max-w-xs">
            {items.map((item, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {item}
              </Badge>
            ))}
          </div>
          {isEditable && (
            <button
              type="button"
              onClick={() => handleStartEditCell(field, key)}
              className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Edit (view only, saved in browser)"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 group">
        {isEmpty ? (
          <span className="text-sm text-gray-400">-</span>
        ) : displayVal.length > 50 ? (
          <div className="truncate max-w-md" title={displayVal}>
            <span className="text-sm text-gray-600">{displayVal}</span>
          </div>
        ) : (
          <span className="text-sm text-gray-600">{displayVal}</span>
        )}
        {isEditable && (
          <button
            type="button"
            onClick={() => handleStartEditCell(field, key)}
            className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 opacity-0 group-hover:opacity-100 transition-opacity"
            title={key === 'remarks' || key === 'Remarks' ? 'Edit and save to database' : 'Edit (view only, saved in browser)'}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span>Fetching field instructions...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-600">
            <p>{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col min-h-0 space-y-4">
      {/* Field Instructions Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Field Instructions</h2>
            <p className="text-sm text-gray-600">
              {fields.length > 0 
                ? `Viewing ${fields.length} field instruction${fields.length === 1 ? '' : 's'}`
                : 'No field instructions available'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Field Instructions View - Show In Validation first (Select All / Unselect All); then other columns */}
      {visibilityError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm flex justify-between items-center">
          <span>{visibilityError}</span>
          <button type="button" onClick={() => setVisibilityError(null)} className="text-red-500 hover:text-red-700">×</button>
        </div>
      )}
      <Card className="overflow-hidden flex flex-col shadow-sm border-0 bg-white w-full max-w-full">
        <CardContent className="p-0 flex flex-col min-h-0 w-full max-w-full overflow-hidden">
          <div className="overflow-auto min-h-0 w-full max-h-[calc(100vh-300px)]">
            <table className="w-full min-w-max border-collapse">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  {subcategory && (
                    <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm whitespace-nowrap bg-gray-100 border-r border-gray-200 w-0">
                      <div className="flex flex-col gap-0.5 min-w-[140px]">
                        <span>Show In Validation</span>
                        {canEditInstructions && (
                          <div className="flex gap-2 text-xs font-normal">
                            <button
                              type="button"
                              onClick={() => handleSelectAllVisibility(true)}
                              disabled={visibilityLoading}
                              className="text-blue-600 hover:underline disabled:opacity-50"
                            >
                              Select All
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              type="button"
                              onClick={() => handleSelectAllVisibility(false)}
                              disabled={visibilityLoading}
                              className="text-blue-600 hover:underline disabled:opacity-50"
                            >
                              Unselect All
                            </button>
                          </div>
                        )}
                      </div>
                    </th>
                  )}
                  {getFieldKeys().map((key) => (
                    <th key={key} className="text-left py-3 px-4 font-semibold text-gray-900 text-sm whitespace-nowrap">
                      {getFieldDisplayName(key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {fields.map((field, index) => (
                  <tr key={index} className="hover:bg-blue-50/30 transition-colors duration-200">
                    {subcategory && (
                      <td className="py-3 px-4 whitespace-nowrap align-top bg-white border-r border-gray-100 w-0">
                        <label className="flex items-center gap-2 cursor-pointer min-w-[120px]">
                          <input
                            type="checkbox"
                            checked={visibilityByKey[getRowKey(field)] !== false}
                            onChange={(e) => handleVisibilityChange(getRowKey(field), e.target.checked)}
                            disabled={!canEditInstructions}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <span className="text-sm text-gray-600 sr-only">Show in validation</span>
                        </label>
                      </td>
                    )}
                    {getFieldKeys().map((key) => (
                      <td key={key} className="py-3 px-4 whitespace-nowrap align-top text-sm">
                        {renderFieldValue(field, key, index)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FieldInstructions;
